<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useAcceptInvitation } from '@/composables/useAcceptInvitation'
import { currentLocale } from '@/i18n'
import PasswordStrengthIndicator from '@/components/organizer/auth/PasswordStrengthIndicator.vue'
import AppSpinner from '@/components/shared/AppSpinner.vue'
import { FAVORITE_COUNTRIES, ALL_COUNTRIES } from '@/types/countries'
import type { AcceptInvitationRegistration } from '@/types/team'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { user, isAuthenticated, logout, fetchUser } = useAuth()
const { metadata, loading, submitting, errors, errorState, fetchByToken, accept } =
  useAcceptInvitation()

const token = computed(() => route.params['token'] as string)

// Registration form fields
const firstName = ref('')
const lastName = ref('')
const password = ref('')
const passwordConfirmation = ref('')
const phone = ref('')
const birthDate = ref('')
const gender = ref<'male' | 'female' | 'non_binary' | 'prefer_not_to_say' | ''>('')
const nationality = ref('')
const clientPasswordMismatch = ref(false)

const loginRedirectUrl = computed(
  () => `/organizer/login?redirect=/accept-invitation/${token.value}`,
)

const emailMismatch = computed<boolean>(() => {
  if (!isAuthenticated.value || !metadata.value || !user.value) return false
  return user.value.email !== metadata.value.invited_email
})

const isEmailMatch = computed<boolean>(() => {
  if (!isAuthenticated.value || !metadata.value || !user.value) return false
  return user.value.email === metadata.value.invited_email
})

// Determine which sub-view to show for a pending invitation
type PendingView =
  | 'register-form'    // not authenticated, requires registration
  | 'login-first'      // not authenticated, has account
  | 'confirm-accept'   // authenticated, email matches
  | 'wrong-account'    // authenticated, email mismatch

const pendingView = computed<PendingView | null>(() => {
  if (!metadata.value || metadata.value.status !== 'pending') return null
  if (!isAuthenticated.value) {
    return metadata.value.requires_registration ? 'register-form' : 'login-first'
  }
  return emailMismatch.value ? 'wrong-account' : 'confirm-accept'
})

async function handleLogout(): Promise<void> {
  await logout()
  window.location.reload()
}

async function handleAccept(): Promise<void> {
  const result = await accept(token.value, {})
  if (result) {
    await fetchUser()
    await router.push({ name: 'organizer.dashboard' })
  }
}

async function handleRegisterAndAccept(): Promise<void> {
  clientPasswordMismatch.value = false

  if (password.value !== passwordConfirmation.value) {
    clientPasswordMismatch.value = true
    return
  }

  if (!gender.value) return

  const registration: AcceptInvitationRegistration = {
    first_name: firstName.value,
    last_name: lastName.value,
    password: password.value,
    password_confirmation: passwordConfirmation.value,
    phone: phone.value,
    birth_date: birthDate.value,
    gender: gender.value,
    nationality: nationality.value,
    preferred_language: currentLocale(),
  }

  const result = await accept(token.value, { register: registration })
  if (result) {
    await fetchUser()
    await router.push({ name: 'organizer.dashboard' })
  }
}

onMounted(() => {
  fetchByToken(token.value)
})
</script>

<template>
  <div class="min-h-screen bg-slate-950 flex items-start justify-center px-4 py-12">
    <div class="w-full max-w-lg">
      <!-- Startline logo / branding -->
      <div class="text-center mb-8">
        <h1 class="text-2xl font-bold text-white">Startline</h1>
      </div>

      <!-- Loading state -->
      <AppSpinner v-if="loading" />

      <template v-else>
        <!-- Not found -->
        <div
          v-if="errorState === 'not-found'"
          class="rounded-2xl border border-slate-700 bg-slate-800/50 p-8 text-center"
        >
          <div class="w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 class="text-lg font-semibold text-white mb-2">{{ t('team.accept.not_found_title') }}</h2>
          <p class="text-sm text-slate-400 mb-6">{{ t('team.accept.not_found_body') }}</p>
          <a href="/" class="inline-flex items-center text-sm font-medium text-emerald-400 hover:text-emerald-300">
            {{ t('team.accept.go_home') }}
          </a>
        </div>

        <!-- Expired -->
        <div
          v-else-if="errorState === 'expired'"
          class="rounded-2xl border border-slate-700 bg-slate-800/50 p-8 text-center"
        >
          <div class="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 class="text-lg font-semibold text-white mb-2">{{ t('team.accept.expired_title') }}</h2>
          <p class="text-sm text-slate-400">{{ t('team.accept.expired_body') }}</p>
        </div>

        <!-- Cancelled -->
        <div
          v-else-if="errorState === 'cancelled'"
          class="rounded-2xl border border-slate-700 bg-slate-800/50 p-8 text-center"
        >
          <div class="w-12 h-12 rounded-full bg-slate-500/10 flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h2 class="text-lg font-semibold text-white mb-2">{{ t('team.accept.cancelled_title') }}</h2>
          <p class="text-sm text-slate-400">{{ t('team.accept.cancelled_body') }}</p>
        </div>

        <!-- Already accepted -->
        <div
          v-else-if="errorState === 'accepted'"
          class="rounded-2xl border border-slate-700 bg-slate-800/50 p-8 text-center"
        >
          <div class="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
            <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 class="text-lg font-semibold text-white mb-2">{{ t('team.accept.already_accepted_title') }}</h2>
          <p class="text-sm text-slate-400">{{ t('team.accept.already_accepted_body') }}</p>
        </div>

        <!-- Pending invitation — branch on auth state -->
        <template v-else-if="metadata && metadata.status === 'pending'">
          <!-- Registration form (not authenticated, needs new account) -->
          <div
            v-if="pendingView === 'register-form'"
            class="rounded-2xl border border-slate-700 bg-slate-800/50 p-8"
          >
            <h2 class="text-xl font-bold text-white mb-1">{{ t('team.accept.register_title') }}</h2>
            <p class="text-sm text-slate-400 mb-6">
              {{ t('team.accept.register_subtitle', {
                org: metadata.organisation_name,
                role: t(`team.roles.${metadata.role}`),
              }) }}
            </p>

            <!-- General API error from accept -->
            <div
              v-if="errors['token'] || errors['register']"
              class="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
            >
              {{ (errors['token'] ?? errors['register'] ?? [])[0] }}
            </div>

            <form novalidate @submit.prevent="handleRegisterAndAccept">
              <div class="space-y-4">
                <!-- Email (read-only) -->
                <div>
                  <label class="block text-xs font-medium text-slate-400 mb-1">
                    {{ t('team.accept.email_label') }}
                  </label>
                  <input
                    type="email"
                    :value="metadata.invited_email"
                    readonly
                    class="block w-full rounded-lg border border-slate-600 px-3 py-2 text-sm text-slate-300 bg-slate-900/50 cursor-not-allowed"
                  />
                </div>

                <!-- First + last name -->
                <div class="grid grid-cols-2 gap-3">
                  <div>
                    <label for="accept_first_name" class="block text-xs font-medium text-slate-400 mb-1">
                      {{ t('team.accept.first_name_label') }}
                    </label>
                    <input
                      id="accept_first_name"
                      v-model="firstName"
                      type="text"
                      required
                      minlength="2"
                      maxlength="100"
                      autocomplete="given-name"
                      class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
                      :class="errors['register.first_name'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
                    />
                    <p v-if="errors['register.first_name']" class="mt-1 text-xs text-red-400">
                      {{ errors['register.first_name'][0] }}
                    </p>
                  </div>
                  <div>
                    <label for="accept_last_name" class="block text-xs font-medium text-slate-400 mb-1">
                      {{ t('team.accept.last_name_label') }}
                    </label>
                    <input
                      id="accept_last_name"
                      v-model="lastName"
                      type="text"
                      required
                      minlength="2"
                      maxlength="100"
                      autocomplete="family-name"
                      class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
                      :class="errors['register.last_name'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
                    />
                    <p v-if="errors['register.last_name']" class="mt-1 text-xs text-red-400">
                      {{ errors['register.last_name'][0] }}
                    </p>
                  </div>
                </div>

                <!-- Password -->
                <div>
                  <label for="accept_password" class="block text-xs font-medium text-slate-400 mb-1">
                    {{ t('team.accept.password_label') }}
                  </label>
                  <input
                    id="accept_password"
                    v-model="password"
                    type="password"
                    required
                    minlength="10"
                    autocomplete="new-password"
                    class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
                    :class="errors['register.password'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
                  />
                  <PasswordStrengthIndicator :password="password" />
                  <p v-if="errors['register.password']" class="mt-1 text-xs text-red-400">
                    {{ errors['register.password'][0] }}
                  </p>
                </div>

                <!-- Password confirmation -->
                <div>
                  <label for="accept_password_confirmation" class="block text-xs font-medium text-slate-400 mb-1">
                    {{ t('team.accept.password_confirmation_label') }}
                  </label>
                  <input
                    id="accept_password_confirmation"
                    v-model="passwordConfirmation"
                    type="password"
                    required
                    autocomplete="new-password"
                    class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
                    :class="clientPasswordMismatch || errors['register.password_confirmation'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
                  />
                  <p v-if="clientPasswordMismatch" class="mt-1 text-xs text-red-400">
                    {{ t('team.accept.password_mismatch') }}
                  </p>
                  <p v-if="errors['register.password_confirmation']" class="mt-1 text-xs text-red-400">
                    {{ errors['register.password_confirmation'][0] }}
                  </p>
                </div>

                <!-- Phone -->
                <div>
                  <label for="accept_phone" class="block text-xs font-medium text-slate-400 mb-1">
                    {{ t('team.accept.phone_label') }}
                  </label>
                  <input
                    id="accept_phone"
                    v-model="phone"
                    type="tel"
                    required
                    autocomplete="tel"
                    class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
                    :class="errors['register.phone'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
                  />
                  <p v-if="errors['register.phone']" class="mt-1 text-xs text-red-400">
                    {{ errors['register.phone'][0] }}
                  </p>
                </div>

                <!-- Birth date -->
                <div>
                  <label for="accept_birth_date" class="block text-xs font-medium text-slate-400 mb-1">
                    {{ t('team.accept.birth_date_label') }}
                  </label>
                  <input
                    id="accept_birth_date"
                    v-model="birthDate"
                    type="date"
                    required
                    class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
                    :class="errors['register.birth_date'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
                  />
                  <p v-if="errors['register.birth_date']" class="mt-1 text-xs text-red-400">
                    {{ errors['register.birth_date'][0] }}
                  </p>
                </div>

                <!-- Gender -->
                <div>
                  <label for="accept_gender" class="block text-xs font-medium text-slate-400 mb-1">
                    {{ t('team.accept.gender_label') }}
                  </label>
                  <select
                    id="accept_gender"
                    v-model="gender"
                    required
                    class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
                    :class="errors['register.gender'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
                  >
                    <option value="" disabled>{{ t('common.select_placeholder') }}</option>
                    <option value="male">{{ t('registration.account.gender_male') }}</option>
                    <option value="female">{{ t('registration.account.gender_female') }}</option>
                    <option value="non_binary">{{ t('registration.account.gender_non_binary') }}</option>
                    <option value="prefer_not_to_say">{{ t('registration.account.gender_prefer_not_to_say') }}</option>
                  </select>
                  <p v-if="errors['register.gender']" class="mt-1 text-xs text-red-400">
                    {{ errors['register.gender'][0] }}
                  </p>
                </div>

                <!-- Nationality -->
                <div>
                  <label for="accept_nationality" class="block text-xs font-medium text-slate-400 mb-1">
                    {{ t('team.accept.nationality_label') }}
                  </label>
                  <select
                    id="accept_nationality"
                    v-model="nationality"
                    required
                    class="block w-full rounded-lg border px-3 py-2 text-sm text-white bg-slate-900 focus:outline-none focus:ring-1 transition-colors"
                    :class="errors['register.nationality'] ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : 'border-slate-600 focus:border-emerald-500 focus:ring-emerald-500'"
                  >
                    <option value="" disabled>{{ t('registration.account.nationality_placeholder') }}</option>
                    <optgroup :label="t('registration.account.nationality_favorites')">
                      <option
                        v-for="country in FAVORITE_COUNTRIES"
                        :key="'fav-' + country.code"
                        :value="country.code"
                      >
                        {{ country.name }}
                      </option>
                    </optgroup>
                    <optgroup :label="t('registration.account.nationality_all')">
                      <option
                        v-for="country in ALL_COUNTRIES"
                        :key="country.code"
                        :value="country.code"
                      >
                        {{ country.name }}
                      </option>
                    </optgroup>
                  </select>
                  <p v-if="errors['register.nationality']" class="mt-1 text-xs text-red-400">
                    {{ errors['register.nationality'][0] }}
                  </p>
                </div>
              </div>

              <button
                type="submit"
                :disabled="submitting"
                class="mt-6 w-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors disabled:opacity-50"
              >
                <span v-if="submitting">{{ t('common.loading') }}</span>
                <span v-else>{{ t('team.accept.create_account_and_join') }}</span>
              </button>
            </form>
          </div>

          <!-- Login first (not authenticated, has account) -->
          <div
            v-else-if="pendingView === 'login-first'"
            class="rounded-2xl border border-slate-700 bg-slate-800/50 p-8 text-center"
          >
            <div class="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </div>
            <h2 class="text-lg font-semibold text-white mb-2">{{ t('team.accept.login_required_title') }}</h2>
            <p class="text-sm text-slate-400 mb-6">
              {{ t('team.accept.login_required_body', {
                org: metadata.organisation_name,
                role: t(`team.roles.${metadata.role}`),
              }) }}
            </p>
            <a
              :href="loginRedirectUrl"
              class="inline-flex items-center justify-center rounded-lg bg-emerald-500 hover:bg-emerald-400 text-white font-semibold px-6 py-2.5 text-sm transition-colors"
            >
              {{ t('team.accept.login_button') }}
            </a>
          </div>

          <!-- Confirm accept (authenticated, email matches) -->
          <div
            v-else-if="pendingView === 'confirm-accept'"
            class="rounded-2xl border border-slate-700 bg-slate-800/50 p-8 text-center"
          >
            <div class="w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h2 class="text-xl font-bold text-white mb-2">
              {{ t('team.accept.confirm_title') }}
            </h2>
            <p class="text-sm text-slate-400 mb-6">
              {{ t('team.accept.confirm_body', {
                org: metadata.organisation_name,
                role: t(`team.roles.${metadata.role}`),
              }) }}
            </p>

            <!-- API error -->
            <div
              v-if="errors['token']"
              class="mb-4 rounded-lg bg-red-500/10 border border-red-500/30 px-4 py-3 text-sm text-red-400"
            >
              {{ errors['token'][0] }}
            </div>

            <button
              type="button"
              :disabled="submitting"
              class="w-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-lg px-5 py-2.5 text-sm transition-colors disabled:opacity-50"
              @click="handleAccept"
            >
              <span v-if="submitting">{{ t('common.loading') }}</span>
              <span v-else>{{ t('team.accept.accept_button') }}</span>
            </button>
          </div>

          <!-- Wrong account (authenticated, email mismatch) -->
          <div
            v-else-if="pendingView === 'wrong-account'"
            class="rounded-2xl border border-slate-700 bg-slate-800/50 p-8 text-center"
          >
            <div class="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
              <svg class="w-6 h-6 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 class="text-lg font-semibold text-white mb-2">{{ t('team.accept.wrong_account_title') }}</h2>
            <p class="text-sm text-slate-400 mb-6">
              {{ t('team.accept.wrong_account_body', {
                current: user?.email ?? '',
                invited: metadata.invited_email,
              }) }}
            </p>
            <button
              type="button"
              class="inline-flex items-center justify-center rounded-lg bg-amber-500 hover:bg-amber-400 text-white font-semibold px-6 py-2.5 text-sm transition-colors"
              @click="handleLogout"
            >
              {{ t('team.accept.logout_and_continue') }}
            </button>
          </div>
        </template>
      </template>
    </div>
  </div>
</template>
